import { useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { assertRowsAffected, toUserMessage } from "@/lib/supabase-errors";

/** Postgres unique-violation → the one-case-per-property rule was hit. */
const DUPLICATE_CASE_MESSAGE =
  "This property already has a Section 8 case in this workspace. Delete the existing case to create a new one.";
const isDuplicateCase = (e: unknown) => (e as { code?: string })?.code === "23505";

export interface Section8Case {
  id: string;
  workspace_id: string;
  pipeline_property_id: string;
  created_by_user_id: string;
  status: string;
  pha_name: string | null;
  pha_phone: string | null;
  pha_email: string | null;
  pha_participant_code: string | null;
  payment_standard: number | null;
  asking_rent: number | null;
  utility_allowance: number | null;
  submission_date: string | null;
  approval_date: string | null;
  tenant_name: string | null;
  tenant_voucher_amount: number | null;
  tenant_phone: string | null;
  tenant_email: string | null;
  tenant_move_in_date: string | null;
  tenant_monthly_salary: number | null;
  hap_contract_start: string | null;
  hap_contract_end: string | null;
  hap_started: boolean | null;
  pha_approved: boolean | null;
  notes: string | null;
  saved_sections: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface HQSInspection {
  id: string;
  workspace_id: string;
  section8_case_id: string;
  inspection_date: string | null;
  inspector_name: string | null;
  inspector_phone: string | null;
  status: string;
  overall_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface HQSChecklistItem {
  id: string;
  inspection_id: string;
  room: string;
  item_name: string;
  passed: boolean | null;
  notes: string | null;
  is_critical: boolean;
}

// Standard HQS checklist template
export const HQS_CHECKLIST_TEMPLATE: { room: string; items: { name: string; critical: boolean }[] }[] = [
  {
    room: "Living Room",
    items: [
      { name: "Ceiling/walls in good condition (no holes, peeling paint, cracks)", critical: false },
      { name: "Floor covering intact and not tripping hazard", critical: false },
      { name: "Window(s) operable and lockable", critical: true },
      { name: "Adequate natural light or working light fixture", critical: false },
      { name: "At least two working electrical outlets", critical: true },
      { name: "Smoke detector present and functional", critical: true },
      { name: "No lead-based paint hazards (pre-1978)", critical: true },
    ],
  },
  {
    room: "Kitchen",
    items: [
      { name: "Working stove/oven with all burners functional", critical: true },
      { name: "Working refrigerator that maintains proper temperature", critical: true },
      { name: "Working sink with hot and cold running water", critical: true },
      { name: "Adequate counter/cabinet space for food prep", critical: false },
      { name: "No evidence of pest infestation", critical: true },
      { name: "GFCI outlet near water source", critical: true },
      { name: "Adequate ventilation (exhaust fan or window)", critical: false },
      { name: "No exposed wiring", critical: true },
    ],
  },
  {
    room: "Bathroom",
    items: [
      { name: "Working toilet that flushes properly", critical: true },
      { name: "Working tub/shower with hot and cold water", critical: true },
      { name: "Working sink with hot and cold water", critical: true },
      { name: "GFCI outlet near water source", critical: true },
      { name: "Adequate ventilation (exhaust fan or window)", critical: false },
      { name: "No water leaks or damage", critical: false },
      { name: "Door with privacy lock", critical: false },
      { name: "No mold or mildew issues", critical: true },
    ],
  },
  {
    room: "Bedroom 1",
    items: [
      { name: "Window that opens, closes, and locks", critical: true },
      { name: "Adequate closet or storage space", critical: false },
      { name: "Ceiling/walls in good condition", critical: false },
      { name: "Working light fixture or two outlets", critical: true },
      { name: "Smoke detector present and functional", critical: true },
      { name: "Door with privacy lock", critical: false },
      { name: "Minimum 70 sq ft for one occupant", critical: true },
    ],
  },
  {
    room: "Bedroom 2",
    items: [
      { name: "Window that opens, closes, and locks", critical: true },
      { name: "Adequate closet or storage space", critical: false },
      { name: "Ceiling/walls in good condition", critical: false },
      { name: "Working light fixture or two outlets", critical: true },
      { name: "Smoke detector present and functional", critical: true },
      { name: "Minimum 70 sq ft for one occupant", critical: true },
    ],
  },
  {
    room: "Exterior/Building",
    items: [
      { name: "Foundation in good condition (no major cracks)", critical: true },
      { name: "Roof in good condition (no active leaks)", critical: true },
      { name: "Gutters and drainage working properly", critical: false },
      { name: "Stairs and railings secure", critical: true },
      { name: "Adequate exterior lighting at entrances", critical: false },
      { name: "Address numbers visible from street", critical: false },
      { name: "No tripping hazards on walkways", critical: false },
      { name: "Carbon monoxide detector (if gas appliances)", critical: true },
    ],
  },
  {
    room: "Systems",
    items: [
      { name: "Heating system operational and adequate", critical: true },
      { name: "Hot water heater functioning (120°F max)", critical: true },
      { name: "Electrical panel properly labeled and accessible", critical: true },
      { name: "No exposed or frayed wiring anywhere", critical: true },
      { name: "Plumbing free of leaks", critical: false },
      { name: "Adequate water pressure throughout", critical: false },
    ],
  },
];

export const S8_STATUSES = [
  { value: "not_started", label: "Not Started", color: "bg-muted text-muted-foreground" },
  { value: "pha_submitted", label: "PHA Submitted", color: "bg-blue-500/10 text-blue-500" },
  { value: "inspection_scheduled", label: "Inspection Scheduled", color: "bg-amber-500/10 text-amber-500" },
  { value: "inspection_passed", label: "Inspection Passed", color: "bg-emerald-500/10 text-emerald-500" },
  { value: "inspection_failed", label: "Inspection Failed", color: "bg-destructive/10 text-destructive" },
  { value: "approved", label: "Approved", color: "bg-primary/10 text-primary" },
  { value: "leased", label: "Leased", color: "bg-emerald-600/10 text-emerald-600" },
];

export function useSection8() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const qc = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  const casesQuery = useQuery({
    queryKey: ["section8-cases", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("section8_cases")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as unknown as Section8Case[];
    },
    enabled: !!workspaceId,
  });

  const createCase = useMutation({
    mutationFn: async (data: { pipeline_property_id: string } & Partial<Section8Case>) => {
      if (!workspaceId || !user) throw new Error("No workspace");
      const { error } = await supabase.from("section8_cases").insert({
        workspace_id: workspaceId,
        created_by_user_id: user.id,
        ...data,
      } as never);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Section 8 case created");
      qc.invalidateQueries({ queryKey: ["section8-cases"] });
    },
    onError: (e) => toast.error(isDuplicateCase(e) ? DUPLICATE_CASE_MESSAGE : toUserMessage(e, "Failed to create case")),
  });

  const updateCase = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<Section8Case>) => {
      const { data: rows, error } = await supabase
        .from("section8_cases")
        .update({ ...data, updated_at: new Date().toISOString() } as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this Section 8 case");
    },
    onSuccess: () => {
      toast.success("Case updated");
      qc.invalidateQueries({ queryKey: ["section8-cases"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update case")),
  });

  /**
   * Upsert a case as the wizard's per-section "Save" persists progress: inserts
   * a draft row on the first save (returning its id) and updates it thereafter,
   * so a half-finished case survives a closed dialog and can be resumed.
   */
  const saveDraft = useMutation({
    mutationFn: async ({ id, ...data }: { id?: string } & { pipeline_property_id: string } & Partial<Section8Case>): Promise<string> => {
      if (!workspaceId || !user) throw new Error("No workspace");
      if (id) {
        const { data: rows, error } = await supabase
          .from("section8_cases")
          .update({ ...data, updated_at: new Date().toISOString() } as never)
          .eq("id", id)
          .select("id");
        if (error) throw error;
        assertRowsAffected(rows, "save this Section 8 case");
        return id;
      }
      const { data: row, error } = await supabase
        .from("section8_cases")
        .insert({ workspace_id: workspaceId, created_by_user_id: user.id, ...data } as never)
        .select("id")
        .single();
      if (error) throw error;
      return (row as { id: string }).id;
    },
    onSuccess: () => {
      toast.success("Progress saved");
      qc.invalidateQueries({ queryKey: ["section8-cases"] });
    },
    onError: (e) => toast.error(isDuplicateCase(e) ? DUPLICATE_CASE_MESSAGE : toUserMessage(e, "Failed to save progress")),
  });

  const deleteCase = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.from("section8_cases").delete().eq("id", id).select("id");
      if (error) throw error;
      assertRowsAffected(data, "delete this Section 8 case");
    },
    onSuccess: () => {
      toast.success("Case deleted");
      qc.invalidateQueries({ queryKey: ["section8-cases"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to delete case")),
  });

  // Inspections
  const inspectionsQuery = useQuery({
    queryKey: ["hqs-inspections", workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data, error } = await supabase
        .from("hqs_inspections")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("inspection_date", { ascending: false });
      if (error) throw error;
      return data as unknown as HQSInspection[];
    },
    enabled: !!workspaceId,
  });

  const createInspection = useMutation({
    mutationFn: async (data: { section8_case_id: string; inspection_date?: string; inspector_name?: string }) => {
      if (!workspaceId) throw new Error("No workspace");
      const { data: inspection, error } = await supabase
        .from("hqs_inspections")
        .insert({ workspace_id: workspaceId, ...data } as never)
        .select()
        .single();
      if (error) throw error;

      // Auto-populate checklist from template
      const items = HQS_CHECKLIST_TEMPLATE.flatMap((room) =>
        room.items.map((item) => ({
          inspection_id: (inspection as any).id,
          room: room.room,
          item_name: item.name,
          is_critical: item.critical,
          passed: null,
        }))
      );
      const { error: itemsError } = await supabase.from("hqs_checklist_items").insert(items as never[]);
      if (itemsError) throw itemsError;
      return inspection;
    },
    onSuccess: () => {
      toast.success("Inspection created with HQS checklist");
      qc.invalidateQueries({ queryKey: ["hqs-inspections"] });
      qc.invalidateQueries({ queryKey: ["hqs-checklist"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to create inspection")),
  });

  const updateInspection = useMutation({
    mutationFn: async ({ id, ...data }: { id: string } & Partial<HQSInspection>) => {
      const { data: rows, error } = await supabase
        .from("hqs_inspections")
        .update({ ...data, updated_at: new Date().toISOString() } as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(rows, "update this inspection");
    },
    onSuccess: () => {
      toast.success("Inspection updated");
      qc.invalidateQueries({ queryKey: ["hqs-inspections"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update inspection")),
  });

  // Checklist
  const getChecklistForInspection = useCallback(async (inspectionId: string) => {
    const { data, error } = await supabase
      .from("hqs_checklist_items")
      .select("*")
      .eq("inspection_id", inspectionId)
      .order("room");
    if (error) throw error;
    return data as unknown as HQSChecklistItem[];
  }, []);

  const updateChecklistItem = useMutation({
    mutationFn: async ({ id, passed, notes }: { id: string; passed: boolean | null; notes?: string }) => {
      const { data, error } = await supabase
        .from("hqs_checklist_items")
        .update({ passed, notes } as never)
        .eq("id", id)
        .select("id");
      if (error) throw error;
      assertRowsAffected(data, "update this checklist item");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hqs-checklist"] });
    },
    onError: (e) => toast.error(toUserMessage(e, "Failed to update checklist item")),
  });

  return {
    cases: casesQuery.data || [],
    isLoading: casesQuery.isLoading,
    inspections: inspectionsQuery.data || [],
    createCase,
    updateCase,
    saveDraft,
    deleteCase,
    createInspection,
    updateInspection,
    getChecklistForInspection,
    updateChecklistItem,
  };
}
